import type { Order, OrderStatus, OrderType } from '@/types';

export type OrderSortKey = 'date-desc' | 'date-asc' | 'total-desc' | 'total-asc';

export interface OrderFilters {
  search: string;
  status: 'all' | OrderStatus;
  dateFrom: string;
  dateTo: string;
  orderType: 'all' | OrderType;
  minTotal: string;
  maxTotal: string;
  sortBy: OrderSortKey;
}

export const DEFAULT_ORDER_FILTERS: OrderFilters = {
  search: '',
  status: 'all',
  dateFrom: '',
  dateTo: '',
  orderType: 'all',
  minTotal: '',
  maxTotal: '',
  sortBy: 'date-desc',
};

export function filterOrdersByDate<T extends { createdAt: string }>(
  items: T[],
  dateFrom?: string,
  dateTo?: string,
): T[] {
  return items.filter((item) => {
    const day = item.createdAt.slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  });
}

export function sortOrders<T extends { createdAt: string; totalPrice: number }>(
  items: T[],
  sortBy: OrderSortKey,
): T[] {
  const copy = [...items];
  copy.sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return a.createdAt.localeCompare(b.createdAt);
      case 'total-desc':
        return Number(b.totalPrice) - Number(a.totalPrice);
      case 'total-asc':
        return Number(a.totalPrice) - Number(b.totalPrice);
      default:
        return b.createdAt.localeCompare(a.createdAt);
    }
  });
  return copy;
}

function matchesSearch(order: Order, query: string): boolean {
  const q = query.trim().toLocaleLowerCase('tr');
  if (!q) return true;

  const haystack = [
    order.id,
    order.note,
    order.restaurant?.name,
    order.restaurant?.username,
    order.wholesaler?.name,
    order.wholesaler?.username,
    ...(order.items ?? []).map((item) => item.productName),
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('tr');

  return haystack.includes(q);
}

export function applyOrderFilters(orders: Order[], filters: OrderFilters): Order[] {
  let list = [...orders];

  if (filters.status !== 'all') {
    list = list.filter((order) => order.status === filters.status);
  }

  list = filterOrdersByDate(list, filters.dateFrom || undefined, filters.dateTo || undefined);

  if (filters.orderType !== 'all') {
    list = list.filter((order) => order.orderType === filters.orderType);
  }

  if (filters.minTotal.trim()) {
    const min = Number(filters.minTotal);
    if (!Number.isNaN(min)) list = list.filter((order) => Number(order.totalPrice) >= min);
  }

  if (filters.maxTotal.trim()) {
    const max = Number(filters.maxTotal);
    if (!Number.isNaN(max)) list = list.filter((order) => Number(order.totalPrice) <= max);
  }

  if (filters.search.trim()) {
    list = list.filter((order) => matchesSearch(order, filters.search));
  }

  return sortOrders(list, filters.sortBy);
}

export function countOrdersByStatus(orders: Order[]): Record<'all' | OrderStatus, number> {
  const counts: Record<'all' | OrderStatus, number> = {
    all: orders.length,
    pending: 0,
    approved: 0,
    completed: 0,
    cancelled: 0,
  };

  for (const order of orders) {
    counts[order.status] += 1;
  }

  return counts;
}

export function hasActiveOrderFilters(filters: OrderFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.status !== 'all' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.orderType !== 'all' ||
    filters.minTotal.trim() !== '' ||
    filters.maxTotal.trim() !== '' ||
    filters.sortBy !== DEFAULT_ORDER_FILTERS.sortBy
  );
}

export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export type DatePresetId = 'today' | 'week' | 'month' | 'all';

export function datePresetRange(preset: DatePresetId): Pick<OrderFilters, 'dateFrom' | 'dateTo'> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (preset === 'all') {
    return { dateFrom: '', dateTo: '' };
  }

  if (preset === 'today') {
    const value = toDateInputValue(today);
    return { dateFrom: value, dateTo: value };
  }

  const from = new Date(today);
  if (preset === 'week') {
    from.setDate(from.getDate() - 6);
  } else {
    from.setDate(from.getDate() - 29);
  }

  return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(today) };
}
